/**
 * Мастер создания акции — 5 шагов с живым превью, порт drawWizard() из консоли.
 *
 * Превью — настоящая серверная страница акции в iframe, а не её имитация:
 * партнёр утверждает именно то, что увидит покупатель. Правила тоже генерирует
 * сервер (той же функцией, что запишет их при запуске), поэтому «превью» и
 * «опубликованное» не могут разойтись.
 */
import type {
  Campaign, CampaignDraft, CampaignPage, PackPrize, PickupPoint, QuizQuestion, Recipe,
  RepeatPeriod, WeightedPrize,
} from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Field, Hash, Icon, Segmented, num, percent, REGIONS } from '@allswipes/ui';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { LangTabs, setTr, tr, type ContentLang } from '../components/LangTabs';
import { MediaField } from '../components/MediaField';
import { useConsole, canPublish } from '../session';
import { useToast } from '../toast';

const STEPS = ['Механика', 'Основное', 'Оформление', 'Условия', 'Запуск'];

/** Регионы платформы. Совпадают с domain.RegionNames на сервере. */
const PAGE_TEMPLATES: Array<{ title: string; body: string }> = [
  {
    title: 'О бренде',
    body: 'Коротко о бренде\n\nЧем занимаемся и почему проводим акции прозрачно.\n\n- Факт первый\n- Факт второй',
  },
  { title: 'Где купить', body: 'Где найти акционный товар\n\n- Сеть магазинов\n- Партнёрские точки' },
  { title: 'О призе', body: 'Что именно можно выиграть\n\nОпиши приз: модель, комплектацию, как и где его выдают.' },
  {
    title: 'Вопросы и ответы',
    body: 'Где найти код?\nОтвет на вопрос.\n\nМожно ввести один код дважды?\nНет, каждый код участвует один раз.',
  },
  { title: 'Пустая страница', body: '' },
];

function isoDay(offset: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.toISOString();
}

// Название бренда сюда не нужно: префикс билета сервер выводит из него сам.
function freshDraft(organizer: string, contact: string, regions: string[]): CampaignDraft {
  return {
    recipe: '',
    name: '',
    tagline: '',
    cta_text: '',
    prize: '',
    winners: 5,
    pts_per_code: 15,
    code_place: 'на этикетке',
    age18: false,
    regions: [...regions],
    start_at: isoDay(1),
    end_at: isoDay(30),
    draw_at: null,
    config: {
      segments: [
        { label: 'iPhone 16', weight: 1 },
        { label: '1000 баллов', weight: 5, points: 1000 },
        { label: 'Скидка −20%', weight: 14, fulfil: 'instant' },
        { label: '100 баллов', weight: 30, points: 100 },
        { label: 'Повезёт в следующий раз', weight: 50, blank: true },
      ],
      scratch_prizes: [
        { label: 'iPhone 16', weight: 1 },
        { label: '500 баллов', weight: 6, points: 500 },
        { label: 'Скидка −25%', weight: 16, fulfil: 'instant' },
        { label: 'Повезёт в следующий раз', weight: 77, blank: true },
      ],
      collect_items: [
        { label: 'Original', weight: 22 },
        { label: 'Zero', weight: 22 },
        { label: 'Vanilla', weight: 16 },
        { label: 'Cherry', weight: 16 },
        { label: 'Lemon', weight: 14 },
        { label: 'Lime', weight: 10 },
      ],
      collect_prize: '',
      plinko_levels: ['50 баллов', '100 баллов', 'Скидка −20%', '1000 баллов', 'iPhone 16'],
      boxes: ['iPhone 16', '1000 баллов', 'Скидка −25%', '100 баллов', 'Фирменный мерч', 'Повезёт в следующий раз'],
      quiz: { reward: 60, questions: [] },
      referral: { you: 100, friend: 50, max: 20 },
      streak: { days: 14, base: 5, step: 5 },
      flash: { spots: 200, prize: '' },
    },
    logo_mode: 'brand',
    logo_url: '',
    pages: [],
    organizer,
    legal_id: '',
    contact,
    handout_text:
      'Мелкие призы и баллы начисляются в приложении сразу. Вещевые призы выдаются в партнёрских точках или доставкой.',
    handout_days: 14,
    rules_text: '',
    video_url: '',
    prize_proof_url: '',
    prize_proof_note: '',
    i18n: {},
  };
}

export function WizardScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useConsole();

  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<CampaignDraft | null>(null);
  const [saved, setSaved] = useState<Campaign | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [rules, setRules] = useState<{ text: string; hash: string } | null>(null);
  const [rulesEdited, setRulesEdited] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'phone' | 'desk'>('phone');
  const [previewTab, setPreviewTab] = useState<'landing' | 'user'>('landing');
  // Справочник областей: список штатов и вилоятов живёт на сервере, чтобы не
  // расходиться с проверкой, которая там же и выполняется.
  const [subregions, setSubregions] = useState<
    Record<string, Array<{ code: string; name: string }>>
  >({});

  useEffect(() => {
    void api
      .catalog()
      .then((c) => setSubregions(c.subregions ?? {}))
      .catch(() => setSubregions({}));
  }, []);

  // Инициализация: новый черновик или загрузка существующего.
  useEffect(() => {
    if (!me) return;
    if (!id) {
      setDraft(freshDraft(me.brand.organizer, me.brand.contact, me.brand.regions));
      return;
    }
    void (async () => {
      try {
        const detail = await api.consoleCampaign(id);
        setSaved(detail.campaign);
        setDraft(campaignToDraft(detail.campaign));
        setRulesEdited(Boolean(detail.campaign.rules_text));
        setStep(2);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Не удалось открыть черновик');
        navigate('/campaigns');
      }
    })();
  }, [id, me, navigate, toast]);

  const recipes = me?.recipes ?? [];
  const recipe = useMemo(() => recipes.find((r) => r.id === draft?.recipe), [recipes, draft?.recipe]);

  const patch = useCallback((changes: Partial<CampaignDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...changes } : prev));
    setFieldErrors({});
  }, []);

  const patchConfig = useCallback((changes: Partial<CampaignDraft['config']>) => {
    setDraft((prev) => (prev ? { ...prev, config: { ...prev.config, ...changes } } : prev));
  }, []);

  // Живое превью и правила: debounce, чтобы набор текста не бил по серверу на
  // каждый символ.
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!draft?.recipe) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      void (async () => {
        try {
          const [html, generated] = await Promise.all([
            api.previewLanding(draft),
            rulesEdited ? Promise.resolve(null) : api.previewRules(draft),
          ]);
          setPreviewHtml(html);
          if (generated) {
            setRules({ text: generated.rules, hash: generated.hash });
          }
        } catch {
          // Превью не критично: оставляем предыдущее, чтобы форма не дёргалась.
        }
      })();
    }, 320);
    return () => window.clearTimeout(timer.current);
  }, [draft, rulesEdited]);

  if (!draft || !me) {
    return (
      <Layout title="Создать акцию">
        <Card>
          <p className="sub">Загружаем…</p>
        </Card>
      </Layout>
    );
  }

  const effectiveRules = rulesEdited ? draft.rules_text : (rules?.text ?? '');

  const save = async (): Promise<Campaign | null> => {
    setBusy(true);
    setFieldErrors({});
    try {
      const payload: CampaignDraft = {
        ...draft,
        id: saved?.id ?? draft.id,
        rules_text: effectiveRules,
      };
      const campaign = await api.saveDraft(payload);
      setSaved(campaign);
      toast('Черновик сохранён');
      return campaign;
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(err.fields ?? {});
        toast(err.message);
      } else {
        toast('Не удалось сохранить');
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    const campaign = await save();
    if (!campaign) return;
    setBusy(true);
    try {
      const result = await api.publish(campaign.id);
      toast(
        result.campaign.status === 'scheduled'
          ? 'Акция запланирована · страница готова'
          : 'Акция запущена · страница готова',
      );
      navigate(`/campaigns/${result.campaign.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(err.fields ?? {});
        toast(err.message);
      } else {
        toast('Не удалось запустить');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title={saved ? 'Черновик акции' : 'Создать акцию'}
      subtitle={recipe ? recipe.name : 'выбери механику — дальше мастер подстроится под неё'}
      action={
        <div className="row" style={{ gap: 8 }}>
          <Button onClick={() => void save()} disabled={busy || !draft.recipe}>
            Сохранить черновик
          </Button>
          {step === 5 && canPublish(me.member.role) && (
            <Button variant="pri" onClick={() => void publish()} disabled={busy}>
              {startsLater(draft.start_at) ? 'Запланировать' : 'Запустить'}
            </Button>
          )}
        </div>
      }
    >
      <nav className="wsteps">
        {STEPS.map((label, i) => (
          <Fragment key={label}>
            {i > 0 && <span className="sep" aria-hidden />}
            <button
              aria-current={step === i + 1 ? 'step' : undefined}
              disabled={i > 0 && !draft.recipe}
              onClick={() => setStep(i + 1)}
            >
              <span className="n">
                {/* Пройденный шаг получает галочку вместо номера — видно не
                    только где ты, но и сколько уже сделано. */}
                {i + 1 < step ? <Icon name="check" size={10} /> : i + 1}
              </span>
              {label}
            </button>
          </Fragment>
        ))}
      </nav>

      <div className="wiz">
        <div className="stack">
          {step === 1 && (
            <StepRecipe
              recipes={recipes}
              value={draft.recipe}
              onPick={(r) => {
                patch({
                  recipe: r.id,
                  draw_at: r.draw ? isoDay(31) : null,
                  config: configFor(draft.config, r),
                });
                setStep(2);
              }}
            />
          )}

          {step === 2 && recipe && (
            <StepMain
              draft={draft}
              recipe={recipe}
              errors={fieldErrors}
              regions={REGIONS}
              subregions={subregions}
              patch={patch}
              patchConfig={patchConfig}
            />
          )}

          {step === 3 && <StepDesign draft={draft} brandHasLogo={Boolean(me.brand.logo_url)} patch={patch} />}

          {step === 4 && (
            <StepTerms
              draft={draft}
              errors={fieldErrors}
              rules={effectiveRules}
              rulesHash={rules?.hash}
              rulesEdited={rulesEdited}
              patch={patch}
              onEditRules={(text) => {
                setRulesEdited(true);
                patch({ rules_text: text });
              }}
              onResetRules={() => {
                setRulesEdited(false);
                patch({ rules_text: '' });
              }}
            />
          )}

          {/* Доказательство призов стоит рядом с условиями выдачи: это про
              одно и то же — чем партнёр подкрепляет обещание. */}
          {step === 4 && <StepProof draft={draft} patch={patch} />}

          {step === 5 && recipe && (
            <StepLaunch
              draft={draft}
              recipe={recipe}
              rules={effectiveRules}
              errors={fieldErrors}
              onGoto={setStep}
            />
          )}

          <div className="row" style={{ gap: 8 }}>
            {step > 1 && (
              <Button onClick={() => setStep(step - 1)} disabled={busy}>
                ← Назад
              </Button>
            )}
            <span className="spacer" />
            {step < 5 && (
              <Button variant="pri" onClick={() => setStep(step + 1)} disabled={!draft.recipe}>
                Дальше →
              </Button>
            )}
          </div>
        </div>

        <aside className="preview">
          {/*
            Две стороны одной акции. Страница — то, что человек увидит, когда
            придёт по ссылке; путь — то, что ему придётся сделать, чтобы
            участвовать. Партнёр собирает вторую, а судят его по первой: на
            странице всё выглядит красиво, а человек упирается в шаг, который
            ему негде пройти. Поэтому обе видны до запуска, а не после.
          */}
          <div className="row" style={{ marginBottom: 10 }}>
            <Segmented
              value={previewTab}
              options={[
                { value: 'landing', label: 'Страница акции' },
                { value: 'user', label: 'Путь участника' },
              ]}
              onChange={setPreviewTab}
            />
            <span className="spacer" />
            {previewTab === 'landing' && (
              <Segmented
                value={previewMode}
                options={[
                  { value: 'phone', label: 'Телефон' },
                  { value: 'desk', label: 'Компьютер' },
                ]}
                onChange={setPreviewMode}
              />
            )}
          </div>

          {previewTab === 'landing' ? (
            <>
              <div className={`pvframe ${previewMode}`}>
                {previewHtml ? (
                  <iframe
                    title="Превью страницы акции"
                    srcDoc={previewHtml}
                    sandbox="allow-scripts"
                  />
                ) : (
                  <div className="empty" style={{ color: 'var(--faint)' }}>
                    Выбери механику — здесь появится страница акции
                  </div>
                )}
              </div>
              <p className="sub" style={{ marginTop: 8, fontSize: 12 }}>
                Это настоящая страница, которую собирает сервер. Покупатель увидит ровно её.
              </p>
            </>
          ) : recipe ? (
            <ParticipantPath draft={draft} recipe={recipe} />
          ) : (
            <div className="empty" style={{ color: 'var(--faint)' }}>
              Выбери механику — здесь появится путь участника
            </div>
          )}
        </aside>
      </div>
    </Layout>
  );
}

/**
 * Путь участника — то же самое, что собрал партнёр, но его глазами.
 *
 * Шаги считаются из черновика, а не из описания механики: «пять дней подряд»
 * и «код под крышкой» — это то, что партнёр только что задал, и увидеть это
 * списком он должен до запуска, а не из жалоб. Здесь же становится видно
 * недостающее: механика с кодом без указанного места кода, анкета из пяти
 * полей ради ста баллов, срок получения в один день.
 *
 * Это подсказка, а не источник истины: решает всё равно сервер, и он же
 * отказывает при запуске. Поэтому здесь нет проверок — только то, что
 * человеку предстоит сделать.
 */
function ParticipantPath({ draft, recipe }: { draft: CampaignDraft; recipe: Recipe }) {
  const config = draft.config;
  const prize = draft.prize?.trim();
  const steps: Array<{ title: string; body: string }> = [];

  // 1. Как человек вообще попадает в акцию.
  if (recipe.id !== 'offer' && (recipe.uses?.includes('in-code') || draft.code_place)) {
    steps.push({
      title: 'Находит код',
      body: draft.code_place
        ? `Покупает товар и находит код ${draft.code_place}.`
        : 'Покупает товар и находит код — но где именно, в акции пока не указано.',
    });
    steps.push({
      title: 'Вводит или сканирует код',
      body: 'В приложении ALLSWIPES или на странице акции. Один код принимается один раз.',
    });
  } else {
    steps.push({
      title: 'Открывает акцию',
      body: 'В приложении или по ссылке со страницы акции. Покупка и код не нужны.',
    });
  }

  // 2. Что человек отдаёт до участия, если бренд об этом просит.
  const fields = config.fields ?? [];
  if (fields.length > 0) {
    const labels = fields
      .map((key) => PROFILE_FIELDS.find((f) => f.key === key)?.label ?? key)
      .join(', ');
    steps.push({
      title: 'Решает, отдавать ли данные',
      body: `Увидит, что бренд просит ${labels}, и свои значения до передачи. Может отказаться — код при этом не тратится.`,
    });
  }

  // 3. Что происходит в самой механике.
  steps.push({ title: 'Участвует', body: participationStep(recipe, draft) });

  // 4. Когда узнаёт результат.
  steps.push(
    recipe.draw
      ? {
          title: 'Ждёт розыгрыша',
          body: draft.draw_at
            ? `Победителей определит публичный сид ${draft.draw_at.slice(0, 10)}. До этого номер билета виден в списке участников.`
            : 'Дата розыгрыша пока не задана.',
        }
      : {
          title: 'Сразу видит результат',
          body: 'Исход считается в момент участия и проверяется в открытом верификаторе.',
        },
  );

  // 5. Что делать с выигрышем.
  if (prize || recipe.prize_mode !== 'quiz') {
    steps.push({
      title: 'Забирает приз',
      body: `${draft.handout_text?.trim() || 'Способ выдачи в акции пока не описан.'} Срок оформления — ${
        draft.handout_days || 0
      } дн.`,
    });
  }

  // 6. Можно ли прийти ещё раз.
  const repeat = config.repeat?.period ?? 'free';
  if (!['quiz', 'streak', 'referral'].includes(recipe.id)) {
    steps.push({
      title: 'Возвращается',
      body:
        repeat === 'never'
          ? 'Участвовать можно один раз за всю акцию.'
          : repeat === 'free'
            ? 'Сколько кодов — столько участий, без ограничения по времени.'
            : `Не чаще, чем раз в ${REPEAT_WORD[repeat] ?? repeat}.`,
    });
  }

  return (
    <>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {steps.map((s, i) => (
          <li key={s.title}>
            <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
              <span className="mono faint" style={{ fontSize: 12 }}>
                {i + 1}
              </span>
              <strong style={{ fontSize: 13.5 }}>{s.title}</strong>
            </div>
            <p className="sub" style={{ margin: '2px 0 10px 22px', fontSize: 12.5 }}>
              {s.body}
            </p>
          </li>
        ))}
      </ol>
      <p className="sub" style={{ marginTop: 4, fontSize: 12 }}>
        Это путь по тому, что уже задано в черновике. Пустое место здесь означает пустое место и у
        человека.
      </p>
    </>
  );
}

const REPEAT_WORD: Record<string, string> = {
  hour: 'час',
  day: 'день',
  week: 'неделю',
  month: 'месяц',
};

/** Что именно происходит в момент участия — по механике и её настройкам. */
function participationStep(recipe: Recipe, draft: CampaignDraft): string {
  const c = draft.config;
  const prize = draft.prize?.trim() || 'приз';
  switch (recipe.id) {
    case 'raffle':
      return 'Получает билет с номером вида CC-04217 — префикс собирается из названия бренда. Номер сразу виден в публичном списке участников.';
    case 'wheel':
      return `Крутит колесо: ${(c.segments ?? []).length || 0} секторов, исход считается от сида и номера прокрута.`;
    case 'scratch':
      return `Стирает карту: ${(c.scratch_prizes ?? []).length || 0} вариантов, приз зашит в карту до стирания.`;
    case 'plinko':
      return 'Бросает фишку — путь по колышкам задают сид и номер броска.';
    case 'mystery':
      return `Выбирает одну коробку из ${(c.boxes ?? []).length || 0}; раскладка зафиксирована хешем до выбора.`;
    case 'collection':
      return `Открывает предмет коллекции. Полный набор из ${(c.collect_items ?? []).length || 0} даёт ${c.collect_prize || prize}.`;
    case 'quiz':
      return `Отвечает на ${(c.quiz?.questions ?? []).length || 0} вопрос(ов) и получает ${c.quiz?.reward ?? 0} баллов. Пройти можно один раз.`;
    case 'streak':
      return `Отмечает вход каждый день: ${c.streak?.days ?? 0} дн. подряд, награда растёт с ${c.streak?.base ?? 0} на ${c.streak?.step ?? 0} за день.`;
    case 'referral':
      return `Отправляет свою ссылку другу. Когда друг выполнит условие, получат оба: ${c.referral?.you ?? 0} и ${c.referral?.friend ?? 0} баллов.`;
    case 'flash':
      return `Занимает одно из ${c.flash?.spots ?? 0} мест. Места кончились — участие больше не засчитывается.`;
    case 'team':
      return `Вносит вклад в общую цель: ${c.team?.goal ?? 0} участий. При достижении ${c.team?.prize || prize} получают все, кто участвовал.`;
    case 'dynamic':
      return `Получает скидку по номеру участия: ${(c.dynamic?.tiers ?? []).length || 0} ступеней, первым — лучшая цена.`;
    case 'offer':
      return `Нажимает «Получить» — предложение открывается сразу: ${c.offer?.reward || 'что именно, в акции пока не указано'}.${
        c.offer?.limit ? ` Всего предложений: ${c.offer.limit}, по одному на человека.` : ' Количество не ограничено.'
      }`;
    case 'data4value':
      return `Передаёт анкету и получает ${c.data?.reward ?? 0} баллов${c.data?.prize ? ` и ${c.data.prize}` : ''}. Один раз на человека.`;
    case 'quest':
      return `Проходит ${(c.quest?.steps ?? []).length || 0} шагов по порядку. Награда за шаг сразу, приз за цепочку — после последнего.`;
    default:
      return `Участвует и получает ${prize}.`;
  }
}

/**
 * Призы, напечатанные на упаковке.
 *
 * У них тираж, а не вес. Веса здесь быть не может: исход определил завод до
 * того, как человек взял товар с полки, и платформа его не считает. Зато она
 * считает другое — сколько таких призов уже выдано, и объявленное число это
 * единственное, с чем выданное можно сравнить.
 */
function PackTable({
  rows,
  error,
  onChange,
}: {
  rows: PackPrize[];
  error?: string;
  onChange: (next: PackPrize[]) => void;
}) {
  const set = (i: number, patch: Partial<PackPrize>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const total = rows.reduce((sum, r) => sum + (r.count || 0), 0);

  return (
    <Field label="Призы на упаковке" error={error}>
      <table className="tbl">
        <thead>
          <tr>
            <th>Что напечатано</th>
            <th style={{ width: 130 }}>Сколько объявлено</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>
                <input
                  className="input"
                  value={row.label}
                  placeholder="Кепка"
                  onChange={(e) => set(i, { label: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="input mono"
                  type="number"
                  min={1}
                  value={row.count || ''}
                  onChange={(e) => set(i, { count: Number(e.target.value) })}
                />
              </td>
              <td>
                <Button size="sm" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
                  ×
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button size="sm" onClick={() => onChange([...rows, { label: '', count: 0 }])}>
          Добавить приз
        </Button>
        <span className="spacer" />
        <span className="sub mono">всего {total}</span>
      </div>
      <p className="sub" style={{ fontSize: 11.5, marginTop: 6 }}>
        Объявленное число попадает в правила и на страницу акции рядом со счётчиком выданного.
        Выдать больше, чем объявлено, платформа не даст — иначе это число стало бы ложью.
      </p>
    </Field>
  );
}

/**
 * Точки выдачи.
 *
 * «Приз выдаётся в точке партнёра» без адреса — это не условие, а отговорка:
 * проверить его нельзя, и спорить с ним тоже. Адрес превращает обещание в
 * проверяемое: человек приехал и либо получил, либо нет.
 *
 * У «приза на упаковке» точка обязательна: человек приходит с крышкой, и
 * других способов выдачи у механики нет. У остальных — по желанию: бывает
 * выдача на кассе по чеку и доставка курьером.
 */
function PickupEditor({
  rows,
  errors,
  required,
  onChange,
}: {
  rows: PickupPoint[];
  errors: Record<string, string>;
  required: boolean;
  onChange: (next: PickupPoint[]) => void;
}) {
  const set = (i: number, patch: Partial<PickupPoint>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <Card>
      <div className="h2" style={{ marginBottom: 6 }}>
        Где забирают приз
      </div>
      <p className="sub" style={{ marginBottom: 12 }}>
        {required
          ? 'Обязательно: человек придёт с упаковкой именно сюда. Адреса попадут в правила и на страницу акции.'
          : 'Адреса попадут в пункт 6.4 правил и на страницу акции. Если приз выдаётся на кассе по чеку или едет курьером — оставь пусто и опиши это в порядке выдачи.'}
      </p>

      {rows.map((row, i) => (
        <div key={i} className="box" style={{ marginBottom: 10, padding: 12 }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input"
              placeholder="Ойла на Рудаки"
              value={row.name}
              onChange={(e) => set(i, { name: e.target.value })}
            />
            <Button size="sm" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
              ×
            </Button>
          </div>
          <input
            className="input"
            style={{ marginTop: 8 }}
            placeholder="Душанбе, проспект Рудаки, 21"
            value={row.address ?? ''}
            onChange={(e) => set(i, { address: e.target.value })}
          />
          {errors[`pickup.${i}`] && (
            <p className="sub" style={{ color: 'var(--bad)', marginTop: 4 }}>
              {errors[`pickup.${i}`]}
            </p>
          )}
          <div className="grid c2" style={{ marginTop: 8 }}>
            <input
              className="input"
              placeholder="Пн–Вс 9:00–22:00"
              value={row.hours ?? ''}
              onChange={(e) => set(i, { hours: e.target.value })}
            />
            <input
              className="input"
              placeholder="вход со двора, нужен паспорт"
              value={row.note ?? ''}
              onChange={(e) => set(i, { note: e.target.value })}
            />
          </div>
        </div>
      ))}

      <Button size="sm" onClick={() => onChange([...rows, { name: '', address: '' }])}>
        Добавить точку
      </Button>
      {errors.pickup && (
        <p className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>
          {errors.pickup}
        </p>
      )}
    </Card>
  );
}

function startsLater(startAt: string): boolean {
  return new Date(startAt).getTime() > Date.now();
}

// ─── шаг 1: механика ───

function StepRecipe({
  recipes,
  value,
  onPick,
}: {
  recipes: Recipe[];
  value: string;
  onPick: (recipe: Recipe) => void;
}) {
  return (
    <Card>
      <div className="h2" style={{ marginBottom: 4 }}>
        Механика акции
      </div>
      <p className="sub" style={{ marginBottom: 14 }}>
        От механики зависит всё остальное: призы, правила и то, как считается исход. После запуска
        механику уже не изменить — только копией.
      </p>
      <div className="recipes">
        {recipes.map((r) => (
          <button
            key={r.id}
            className="rcard"
            aria-pressed={value === r.id}
            disabled={!r.implemented}
            onClick={() => onPick(r)}
            title={r.implemented ? r.how : 'Эта механика ещё в разработке'}
          >
            <div className="row" style={{ marginBottom: 6 }}>
              <span className="rname" style={{ flex: 1 }}>
                {r.name}
              </span>
              {!r.implemented && <Badge>скоро</Badge>}
              {r.implemented && r.draw && <Badge tone="good">розыгрыш</Badge>}
            </div>
            <div className="sub" style={{ fontSize: 12.5 }}>
              {r.tag}
            </div>
            <div className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>
              Лучше всего для: {r.best}
            </div>
            <div className="faint" style={{ fontSize: 11.5, marginTop: 3 }}>
              Как выигрывают: {r.how}
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}

// configFor оставляет от настроек только то, что умеет новая механика.
//
// Партнёр может пройти мастер до середины, вернуться на первый шаг и выбрать
// другую механику. Остатки прошлой сервер всё равно отбрасывает при сохранении
// (api/internal/domain/config_recipe.go), но превью и правила рисуются из
// черновика в браузере — и без чистки показывали бы условия, которых у акции
// уже нет.
function configFor(config: CampaignDraft['config'], recipe: Recipe): CampaignDraft['config'] {
  const next: CampaignDraft['config'] = { fields: config.fields };

  switch (recipe.prize_mode) {
    case 'table':
      if (recipe.id === 'scratch') next.scratch_prizes = config.scratch_prizes;
      else next.segments = config.segments;
      break;
    case 'plinko':
      next.plinko_levels = config.plinko_levels;
      break;
    case 'boxes':
      next.boxes = config.boxes;
      break;
    case 'collect':
      next.collect_items = config.collect_items;
      next.collect_prize = config.collect_prize;
      break;
    case 'quiz':
      next.quiz = config.quiz;
      break;
    case 'referral':
      next.referral = config.referral;
      break;
    case 'streak':
      next.streak = config.streak;
      break;
    case 'flash':
      next.flash = config.flash;
      break;
    case 'team':
      next.team = config.team;
      break;
    case 'data':
      next.data = config.data;
      break;
    case 'dynamic':
      next.dynamic = config.dynamic;
      break;
    case 'quest':
      // Шаги цепочки опираются на квиз и серию: это её настройки, а не
      // остатки чужой механики.
      next.quest = config.quest;
      next.quiz = config.quiz;
      next.streak = config.streak;
      break;
  }

  // Перенос срока и переигровка места есть только у акции с розыгрышем.
  if (recipe.draw) {
    next.threshold = config.threshold;
    next.reclaim = config.reclaim;
  }
  // У квиза, серии и приглашений частоту задаёт сама механика.
  if (!['quiz', 'streak', 'referral'].includes(recipe.id)) {
    next.repeat = config.repeat;
  }
  return next;
}

// ─── шаг 2: основное ───

function StepMain({
  draft,
  recipe,
  errors,
  regions,
  subregions,
  patch,
  patchConfig,
}: {
  draft: CampaignDraft;
  recipe: Recipe;
  errors: Record<string, string>;
  regions: Array<{ code: string; name: string }>;
  subregions: Record<string, Array<{ code: string; name: string }>>;
  patch: (changes: Partial<CampaignDraft>) => void;
  patchConfig: (changes: Partial<CampaignDraft['config']>) => void;
}) {
  const [lang, setLang] = useState<ContentLang>('ru');

  const toggleRegion = (code: string) => {
    const on = draft.regions.includes(code);
    const next = on ? draft.regions.filter((r) => r !== code) : [...draft.regions, code];
    // Убрали страну — убираем и её области: «акция в Калифорнии» без США в
    // территории не значит ничего, а в правила попадёт.
    const subs = on
      ? (draft.subregions ?? []).filter((sub: string) => !sub.startsWith(code + '-'))
      : draft.subregions;
    patch({ regions: next, subregions: subs });
  };

  const toggleSubregion = (code: string) => {
    const current = draft.subregions ?? [];
    const next = current.includes(code)
      ? current.filter((s) => s !== code)
      : [...current, code];
    patch({ subregions: next });
  };

  // Поле на выбранном языке: русский правит сам черновик, перевод — i18n.
  const field = (name: 'name' | 'tagline', base: string) => ({
    value: tr(draft.i18n, lang, name, base),
    onChange: (e: { target: { value: string } }) =>
      lang === 'ru'
        ? patch({ [name]: e.target.value } as Partial<CampaignDraft>)
        : patch({ i18n: setTr(draft.i18n, lang, name, e.target.value) }),
  });

  return (
    <>
      <Card>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="h2">Основное</span>
          <span className="spacer" />
          <LangTabs lang={lang} onChange={setLang} i18n={draft.i18n} />
        </div>

        {lang !== 'ru' && (
          <p className="sub" style={{ marginBottom: 12 }}>
            Пустое поле означает «показывать русский текст»: покупатель никогда не увидит пустой
            заголовок. Переводи то, что стоит перевести.
          </p>
        )}

        <Field label="Название акции" error={lang === 'ru' ? errors.name : undefined}>
          <input className="input" placeholder="iPhone под крышкой" {...field('name', draft.name)} />
        </Field>
        <Field label="Подзаголовок" hint="Одна строка под заголовком на странице акции">
          <input
            className="input"
            placeholder="Найди код под крышкой — выиграй один из 50 смартфонов"
            {...field('tagline', draft.tagline)}
          />
        </Field>

        <PrizeEditor draft={draft} recipe={recipe} errors={errors} patch={patch} patchConfig={patchConfig} />

        <Field label="Баллов за каждый код" hint="Начисляются всем участникам, независимо от выигрыша">
          <input
            className="input mono"
            type="number"
            min={0}
            value={draft.pts_per_code}
            onChange={(e) => patch({ pts_per_code: Number(e.target.value) })}
          />
        </Field>

        <Field label="Где напечатан код" error={errors.code_place}>
          <input
            className="input"
            value={draft.code_place}
            onChange={(e) => patch({ code_place: e.target.value })}
            placeholder="под крышкой"
          />
        </Field>
      </Card>

      <Card>
        <div className="h2" style={{ marginBottom: 12 }}>
          Где и когда
        </div>
        <Field label="Регионы" error={errors.regions}>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {regions.map(({ code, name: label }) => (
              <button
                key={code}
                className="badge"
                aria-pressed={draft.regions.includes(code)}
                style={
                  draft.regions.includes(code)
                    ? { background: 'var(--accent)', color: 'var(--accent-ink)', borderColor: 'transparent' }
                    : undefined
                }
                onClick={() => toggleRegion(code)}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        {/* Разбивка внутри страны. Показываем только для выбранных стран, у
            которых она есть: промо в Таджикистане — это промо по всей стране,
            а в США акция сети магазинов живёт в двух-трёх штатах. Ничего не
            выбрано — акция идёт по всей стране, как раньше. */}
        {draft.regions
          .filter((code) => (subregions[code] ?? []).length > 0)
          .map((code) => (
            <Field
              key={code}
              label={`Области · ${regions.find((r) => r.code === code)?.name ?? code}`}
              hint="Ничего не выбрано — акция идёт по всей стране. Выбранные области попадут в правила и в ленту."
              error={errors.subregions}
            >
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {(subregions[code] ?? []).map((sub) => (
                  <button
                    key={sub.code}
                    className="badge"
                    aria-pressed={(draft.subregions ?? []).includes(sub.code)}
                    style={
                      (draft.subregions ?? []).includes(sub.code)
                        ? {
                            background: 'var(--accent)',
                            color: 'var(--accent-ink)',
                            borderColor: 'transparent',
                          }
                        : undefined
                    }
                    onClick={() => toggleSubregion(sub.code)}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            </Field>
          ))}

        <div className="grid c2">
          <Field label="Начало" error={errors.start_at}>
            <input
              className="input"
              type="date"
              value={toDateInput(draft.start_at)}
              onChange={(e) => patch({ start_at: fromDateInput(e.target.value) })}
            />
          </Field>
          <Field label="Окончание приёма кодов" error={errors.end_at}>
            <input
              className="input"
              type="date"
              value={toDateInput(draft.end_at)}
              onChange={(e) => patch({ end_at: fromDateInput(e.target.value) })}
            />
          </Field>
        </div>

        {recipe.draw && (
          <Field
            label="Дата и время розыгрыша"
            error={errors.draw_at}
            hint="Розыгрыш не может быть раньше окончания приёма кодов: список билетов фиксируется до того, как станет известен публичный сид."
          >
            <input
              className="input"
              type="datetime-local"
              value={toDateTimeInput(draft.draw_at)}
              onChange={(e) => patch({ draw_at: fromDateTimeInput(e.target.value) })}
            />
          </Field>
        )}

        <label className="row" style={{ gap: 8, marginTop: 4 }}>
          <input
            type="checkbox"
            checked={draft.age18}
            onChange={(e) => patch({ age18: e.target.checked })}
          />
          <span>Только для участников 18+</span>
        </label>
      </Card>

      <RulesEditor draft={draft} recipe={recipe} errors={errors} patchConfig={patchConfig} />
    </>
  );
}

/**
 * Правила участия: сколько раз можно участвовать, какие данные запрашиваются,
 * что делать с незабранным призом и когда переносится срок.
 *
 * Всё это попадает в правила акции и на её страницу. Поэтому редактор стоит
 * здесь, рядом со сроками, а не прячется в «дополнительно»: партнёр должен
 * увидеть эти решения до запуска, а не обнаружить их поведение потом.
 */
function RulesEditor({
  draft,
  recipe,
  errors,
  patchConfig,
}: {
  draft: CampaignDraft;
  recipe: Recipe;
  errors: Record<string, string>;
  patchConfig: (changes: Partial<CampaignDraft['config']>) => void;
}) {
  const config = draft.config;
  const repeat = config.repeat?.period ?? 'free';
  const fields = config.fields ?? [];
  const threshold = config.threshold;
  const reclaim = config.reclaim;

  // У квиза, серии и приглашений частоту задаёт сама механика.
  const repeatOwned = ['quiz', 'streak', 'referral'].includes(recipe.id);

  const toggleField = (key: string) => {
    const next = fields.includes(key) ? fields.filter((f) => f !== key) : [...fields, key];
    patchConfig({ fields: next });
  };

  return (
    <>
      <Card>
        <div className="h2" style={{ marginBottom: 6 }}>
          Повторное участие
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          Код и так одноразовый. Это ограничение — про человека: у кого-то пачка чеков, и без
          правила один участник забирает всё.
        </p>
        {repeatOwned ? (
          <p className="sub">
            У механики «{recipe.name}» частоту задаёт она сама: квиз проходится один раз, день
            серии отмечается раз в сутки, приглашения не ограничены.
          </p>
        ) : (
          <Field label="Как часто один человек может участвовать" error={errors.repeat}>
            <select
              className="select"
              value={repeat}
              onChange={(e) => patchConfig({ repeat: { period: e.target.value as RepeatPeriod } })}
            >
              <option value="free">Без ограничений</option>
              <option value="never">Один раз</option>
              <option value="hour">Не чаще раза в час</option>
              <option value="day">Не чаще раза в день</option>
              <option value="week">Не чаще раза в неделю</option>
              <option value="month">Не чаще раза в месяц</option>
            </select>
          </Field>
        )}
      </Card>

      <Card>
        <div className="h2" style={{ marginBottom: 6 }}>
          Данные участника
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          До пяти полей. Участник увидит список и сами значения до передачи и сможет отказаться —
          код при этом не потратится. Запрошенные поля попадают в правила акции.
        </p>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          {PROFILE_FIELDS.map((f) => (
            <button
              key={f.key}
              type="button"
              className="badge"
              aria-pressed={fields.includes(f.key)}
              title={f.why}
              style={
                fields.includes(f.key)
                  ? { background: 'var(--accent)', color: 'var(--accent-ink)', borderColor: 'var(--accent)' }
                  : undefined
              }
              onClick={() => toggleField(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {errors.fields && (
          <p className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {errors.fields}
          </p>
        )}
      </Card>

      <PickupEditor
        rows={config.pickup ?? []}
        errors={errors}
        required={recipe.prize_mode === 'pack'}
        onChange={(pickup) => patchConfig({ pickup })}
      />

      {recipe.draw && (
        <>
          <Card>
            <div className="h2" style={{ marginBottom: 6 }}>
              Если приз не забрали
            </div>
            <p className="sub" style={{ marginBottom: 12 }}>
              Запасные разыгрываются сразу вместе с победителями, тем же сидом, и публикуются с
              самого начала. Поэтому переход места — не новый розыгрыш, а следующий номер из уже
              опубликованного списка.
            </p>
            <label className="row" style={{ gap: 8, marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={Boolean(reclaim?.redraw)}
                onChange={(e) =>
                  patchConfig({
                    reclaim: e.target.checked
                      ? { redraw: true, after_days: reclaim?.after_days ?? 14, reserve: reclaim?.reserve ?? 5 }
                      : undefined,
                  })
                }
              />
              <span>Переигрывать место, если приз не забрали в срок</span>
            </label>
            {reclaim?.redraw && (
              <div className="grid c2">
                <Field label="Дней на оформление" error={errors.reclaim_days}>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    max={60}
                    value={reclaim.after_days}
                    onChange={(e) =>
                      patchConfig({ reclaim: { ...reclaim, after_days: Number(e.target.value) } })
                    }
                  />
                </Field>
                <Field label="Сколько запасных" error={errors.reclaim_reserve}>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    max={50}
                    value={reclaim.reserve}
                    onChange={(e) =>
                      patchConfig({ reclaim: { ...reclaim, reserve: Number(e.target.value) } })
                    }
                  />
                </Field>
              </div>
            )}
          </Card>

          <Card>
            <div className="h2" style={{ marginBottom: 6 }}>
              Если участников меньше, чем нужно
            </div>
            <p className="sub" style={{ marginBottom: 12 }}>
              Розыгрыш ста призов на двадцати участниках невыгоден никому. Условие и предельное
              число переносов объявляются в правилах до старта, а каждый перенос публикуется на
              странице акции — иначе это выглядит как подкрутка сроков.
            </p>
            <label className="row" style={{ gap: 8, marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={Boolean(threshold)}
                onChange={(e) =>
                  patchConfig({
                    threshold: e.target.checked
                      ? {
                          min_participants: threshold?.min_participants ?? 100,
                          extend_days: threshold?.extend_days ?? 7,
                          max_extends: threshold?.max_extends ?? 2,
                        }
                      : undefined,
                  })
                }
              />
              <span>Переносить срок, если не набралось участников</span>
            </label>
            {threshold && (
              <div className="grid c3">
                <Field label="Нужно участников" error={errors.threshold}>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    value={threshold.min_participants}
                    onChange={(e) =>
                      patchConfig({
                        threshold: { ...threshold, min_participants: Number(e.target.value) },
                      })
                    }
                  />
                </Field>
                <Field label="Перенос, дней" error={errors.threshold_days}>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    max={30}
                    value={threshold.extend_days}
                    onChange={(e) =>
                      patchConfig({ threshold: { ...threshold, extend_days: Number(e.target.value) } })
                    }
                  />
                </Field>
                <Field label="Не больше раз" error={errors.threshold_max}>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    max={3}
                    value={threshold.max_extends}
                    onChange={(e) =>
                      patchConfig({ threshold: { ...threshold, max_extends: Number(e.target.value) } })
                    }
                  />
                </Field>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}

/**
 * Каталог полей анкеты — тот же, что на сервере (api/internal/domain/profile.go).
 *
 * Дублируется подписями, а не тянется запросом: это восемь строк, которые
 * меняются раз в год, а лишний запрос на экране мастера стоит дороже.
 */
const PROFILE_FIELDS: Array<{ key: string; label: string; why: string }> = [
  { key: 'name', label: 'Имя', why: 'Обратиться по имени при выдаче приза' },
  { key: 'city', label: 'Город', why: 'Предложить ближайшую точку выдачи' },
  { key: 'birthday', label: 'Дата рождения', why: 'Возрастные ограничения и поздравления' },
  { key: 'gender', label: 'Пол', why: 'Понять, кому интересна акция' },
  { key: 'email', label: 'Почта', why: 'Второй способ связи' },
  { key: 'kids', label: 'Дети в семье', why: 'Детские товары подбираются по возрасту' },
  { key: 'car', label: 'Автомобиль', why: 'Акции заправок и автотоваров' },
  { key: 'favorite', label: 'Любимый продукт', why: 'Что выпускать и что класть в приз' },
];

/** Редактор призов — форма зависит от prize_mode механики, как в прототипе. */
function PrizeEditor({
  draft,
  recipe,
  errors,
  patch,
  patchConfig,
}: {
  draft: CampaignDraft;
  recipe: Recipe;
  errors: Record<string, string>;
  patch: (changes: Partial<CampaignDraft>) => void;
  patchConfig: (changes: Partial<CampaignDraft['config']>) => void;
}) {
  switch (recipe.prize_mode) {
    case 'simple':
      return (
        <>
          <Field label="Призовой фонд" error={errors.prize}>
            <input
              className="input"
              value={draft.prize}
              onChange={(e) => patch({ prize: e.target.value })}
              placeholder="50 × iPhone 16"
            />
          </Field>
          <Field label="Количество победителей" error={errors.winner_count}>
            <input
              className="input mono"
              type="number"
              min={1}
              value={draft.winners}
              onChange={(e) => patch({ winners: Number(e.target.value) })}
            />
          </Field>
        </>
      );

    case 'offer': {
      const o = draft.config.offer ?? { reward: '', url: '', limit: 0 };
      const set = (patch: Partial<typeof o>) => patchConfig({ offer: { ...o, ...patch } });
      return (
        <>
          <Field label="Что человек получит" error={errors.offer_reward}>
            <input
              className="input"
              value={o.reward}
              placeholder="6 месяцев Apple Music за полцены"
              onChange={(e) => set({ reward: e.target.value })}
            />
          </Field>
          <Field label="Ссылка на предложение" error={errors.offer_url}>
            <input
              className="input mono"
              value={o.url ?? ''}
              placeholder="https://music.apple.com/promo/..."
              onChange={(e) => set({ url: e.target.value })}
            />
          </Field>
          <p className="sub" style={{ fontSize: 11.5, marginTop: -6, marginBottom: 10 }}>
            Только https: по этой ссылке человек уходит с платформы, и отправлять его по
            незащищённому адресу мы не вправе. Партнёрская ссылка тоже подойдёт — для человека
            разницы нет.
          </p>
          <Field label="Сколько предложений всего — необязательно" error={errors.offer_limit}>
            <input
              className="input mono"
              type="number"
              min={0}
              value={o.limit || ''}
              placeholder="оставь пустым, если без ограничения"
              onChange={(e) => set({ limit: Number(e.target.value) })}
            />
          </Field>
          <p className="sub" style={{ fontSize: 11.5, marginTop: -6 }}>
            Пусто — предложение получат все, кто придёт, и счётчик покажет только выданное. Если
            число есть, человек увидит обе границы до нажатия: сколько было и сколько осталось.
            «Осталось 12» — это состояние счётчика, а не приём продавца, поэтому выдуманное
            число сюда ставить не надо.
          </p>
        </>
      );
    }

    case 'pack': {
      const rows = draft.config.pack ?? [];
      return <PackTable rows={rows} error={errors.pack} onChange={(next) => patchConfig({ pack: next })} />;
    }

    case 'table': {
      const key = recipe.id === 'scratch' ? 'scratch_prizes' : 'segments';
      const rows = (draft.config[key] ?? []) as WeightedPrize[];
      return (
        <WeightTable
          label={recipe.id === 'scratch' ? 'Призы скретч-карты' : 'Секторы колеса'}
          rows={rows}
          error={errors.prizes}
          onChange={(next) => patchConfig({ [key]: next } as Partial<CampaignDraft['config']>)}
        />
      );
    }

    case 'collect': {
      const rows = draft.config.collect_items ?? [];
      return (
        <>
          <WeightTable
            label="Предметы коллекции"
            rows={rows}
            error={errors.prizes}
            onChange={(next) => patchConfig({ collect_items: next })}
          />
          <Field label="Приз за полный набор">
            <input
              className="input"
              value={draft.config.collect_prize ?? ''}
              onChange={(e) => patchConfig({ collect_prize: e.target.value })}
            />
          </Field>
        </>
      );
    }

    case 'plinko':
      return (
        <ListEditor
          label="Слоты плинко (центр → край)"
          hint="Вероятности задаёт сама доска и изменить их нельзя — так партнёр не может подкрутить шансы."
          items={draft.config.plinko_levels ?? []}
          onChange={(next) => patchConfig({ plinko_levels: next })}
        />
      );

    case 'boxes':
      return (
        <ListEditor
          label="Коробки"
          hint="Шанс выбрать любую коробку одинаков."
          items={draft.config.boxes ?? []}
          onChange={(next) => patchConfig({ boxes: next })}
        />
      );

    case 'quiz': {
      const quiz = draft.config.quiz ?? { reward: 0, questions: [] };
      const setQuiz = (changes: Partial<typeof quiz>) =>
        patchConfig({ quiz: { ...quiz, ...changes } });
      const setQuestion = (index: number, changes: Partial<QuizQuestion>) =>
        setQuiz({
          questions: quiz.questions.map((q, i) => (i === index ? { ...q, ...changes } : q)),
        });

      return (
        <div className="stack">
          <Field label="Баллов за прохождение квиза" error={errors['quiz_reward']}>
            <input
              className="input mono"
              type="number"
              min={0}
              value={quiz.reward}
              onChange={(e) => setQuiz({ reward: Number(e.target.value) })}
            />
          </Field>

          {quiz.questions.map((q, qi) => (
            <Card key={qi}>
              <div className="row" style={{ marginBottom: 8 }}>
                <span className="lab">Вопрос {qi + 1}</span>
                <span className="spacer" />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="close"
                  onClick={() => setQuiz({ questions: quiz.questions.filter((_, i) => i !== qi) })}
                >
                  убрать
                </Button>
              </div>
              <Field label="Текст вопроса" error={errors[`quiz.${qi}`]}>
                <input
                  className="input"
                  value={q.text}
                  placeholder="В каком году основан бренд?"
                  onChange={(e) => setQuestion(qi, { text: e.target.value })}
                />
              </Field>
              <div className="stack" style={{ marginTop: 10 }}>
                {q.options.map((option, oi) => (
                  <div key={oi} className="row" style={{ gap: 8 }}>
                    {/* Правильный ответ отмечается здесь и только здесь:
                        в приложение он не уходит, сверяет его сервер. */}
                    <label className="row" style={{ gap: 6, flex: 1 }}>
                      <input
                        type="radio"
                        checked={q.answer === oi}
                        onChange={() => setQuestion(qi, { answer: oi })}
                      />
                      <input
                        className="input"
                        value={option}
                        placeholder={`Вариант ${String.fromCharCode(65 + oi)}`}
                        onChange={(e) =>
                          setQuestion(qi, {
                            options: q.options.map((o, i) => (i === oi ? e.target.value : o)),
                          })
                        }
                      />
                    </label>
                    {q.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="close"
                        ariaLabel="убрать вариант"
                        onClick={() =>
                          setQuestion(qi, {
                            options: q.options.filter((_, i) => i !== oi),
                            answer: q.answer > oi ? q.answer - 1 : Math.min(q.answer, q.options.length - 2),
                          })
                        }
                      />
                    )}
                  </div>
                ))}
                {q.options.length < 6 && (
                  <Button
                    variant="out"
                    size="sm"
                    icon="plus"
                    onClick={() => setQuestion(qi, { options: [...q.options, ''] })}
                  >
                    Вариант
                  </Button>
                )}
              </div>
              <Field label="Пояснение после ответа" hint="Показывается всем — и тем, кто ответил неверно">
                <input
                  className="input"
                  value={q.explain ?? ''}
                  placeholder="Марка основана в 1886 году."
                  onChange={(e) => setQuestion(qi, { explain: e.target.value })}
                />
              </Field>
            </Card>
          ))}

          <Button
            variant="out"
            icon="plus"
            onClick={() =>
              setQuiz({
                questions: [
                  ...quiz.questions,
                  { text: '', options: ['', ''], answer: 0, explain: '' },
                ],
              })
            }
          >
            Добавить вопрос
          </Button>
          <p className="sub">
            Правильные ответы хранятся на сервере и не приходят в приложение до ответа. Баллы
            начисляются за участие, а не за правильность — так квиз не превращается в экзамен.
          </p>
        </div>
      );
    }

    case 'referral': {
      const r = draft.config.referral ?? { you: 0, friend: 0, max: 0 };
      return (
        <div className="grid c3">
          <Field label="Тебе за друга">
            <input
              className="input mono"
              type="number"
              value={r.you}
              onChange={(e) => patchConfig({ referral: { ...r, you: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Другу">
            <input
              className="input mono"
              type="number"
              value={r.friend}
              onChange={(e) => patchConfig({ referral: { ...r, friend: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Максимум друзей">
            <input
              className="input mono"
              type="number"
              value={r.max}
              onChange={(e) => patchConfig({ referral: { ...r, max: Number(e.target.value) } })}
            />
          </Field>
        </div>
      );
    }

    case 'streak': {
      const s = draft.config.streak ?? { days: 7, base: 5, step: 5 };
      return (
        <div className="grid c3">
          <Field label="Дней в серии">
            <input
              className="input mono"
              type="number"
              value={s.days}
              onChange={(e) => patchConfig({ streak: { ...s, days: Number(e.target.value) } })}
            />
          </Field>
          <Field label="База">
            <input
              className="input mono"
              type="number"
              value={s.base}
              onChange={(e) => patchConfig({ streak: { ...s, base: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Шаг">
            <input
              className="input mono"
              type="number"
              value={s.step}
              onChange={(e) => patchConfig({ streak: { ...s, step: Number(e.target.value) } })}
            />
          </Field>
        </div>
      );
    }

    case 'flash': {
      const f = draft.config.flash ?? { spots: 100, prize: '' };
      return (
        <>
          <Field label="Приз">
            <input
              className="input"
              value={f.prize}
              onChange={(e) => patchConfig({ flash: { ...f, prize: e.target.value } })}
            />
          </Field>
          <Field label="Сколько мест">
            <input
              className="input mono"
              type="number"
              value={f.spots}
              onChange={(e) => patchConfig({ flash: { ...f, spots: Number(e.target.value) } })}
            />
          </Field>
        </>
      );
    }

    case 'team': {
      const t = draft.config.team ?? { goal: 100, prize: '' };
      return (
        <>
          <Field
            label="Приз каждому участнику"
            hint="Его получат все, кто внёс вклад, — включая тех, кто участвовал первым"
            error={errors.team_prize}
          >
            <input
              className="input"
              value={t.prize}
              onChange={(e) => patchConfig({ team: { ...t, prize: e.target.value } })}
              placeholder="Фирменная термокружка"
            />
          </Field>
          <Field
            label="Общая цель, участий"
            hint="Цель считается в участиях, а не в людях: так её видно по счётчику"
            error={errors.team_goal}
          >
            <input
              className="input mono"
              type="number"
              min={2}
              value={t.goal}
              onChange={(e) => patchConfig({ team: { ...t, goal: Number(e.target.value) } })}
            />
          </Field>
        </>
      );
    }

    case 'data': {
      const d = draft.config.data ?? { reward: 100, prize: '' };
      return (
        <>
          <Field
            label="Награда за анкету"
            hint="Начисляется один раз. Какие поля запрашиваются — на шаге «Условия»"
            error={errors.data_reward}
          >
            <input
              className="input mono"
              type="number"
              min={0}
              value={d.reward}
              onChange={(e) => patchConfig({ data: { ...d, reward: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Или приз вместо баллов" hint="Пусто — награда только баллами">
            <input
              className="input"
              value={d.prize ?? ''}
              onChange={(e) => patchConfig({ data: { ...d, prize: e.target.value } })}
              placeholder="Купон на кофе"
            />
          </Field>
        </>
      );
    }

    case 'dynamic': {
      const tiers = draft.config.dynamic?.tiers ?? [];
      const setTiers = (next: typeof tiers) => patchConfig({ dynamic: { tiers: next } });
      return (
        <>
          <div className="lab" style={{ marginBottom: 8 }}>
            Ступени скидки — первым достаётся лучшая цена
          </div>
          {tiers.map((tier, i) => (
            <div key={i} className="row" style={{ gap: 8, marginBottom: 8 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                value={tier.label}
                placeholder="−30%"
                onChange={(e) =>
                  setTiers(tiers.map((t, j) => (i === j ? { ...t, label: e.target.value } : t)))
                }
              />
              <input
                className="input mono"
                style={{ width: 110 }}
                type="number"
                value={tier.until}
                title="До какого участника действует ступень"
                onChange={(e) =>
                  setTiers(tiers.map((t, j) => (i === j ? { ...t, until: Number(e.target.value) } : t)))
                }
              />
              <Button size="sm" variant="danger" onClick={() => setTiers(tiers.filter((_, j) => j !== i))}>
                ×
              </Button>
            </div>
          ))}
          {errors.tiers && <div className="err">{errors.tiers}</div>}
          <Button
            size="sm"
            icon="plus"
            onClick={() =>
              setTiers([...tiers, { label: '', until: (tiers[tiers.length - 1]?.until ?? 0) + 50 }])
            }
          >
            Добавить ступень
          </Button>
          <p className="sub" style={{ marginTop: 8 }}>
            Границы растут: первая ступень действует до своего числа участий, вторая — до своего, и
            так далее. Когда ступени кончатся, акция перестанет принимать коды, и об этом будет
            сказано прямо.
          </p>
        </>
      );
    }

    case 'quest': {
      const q = draft.config.quest ?? { steps: [], prize: '' };
      const setSteps = (steps: typeof q.steps) => patchConfig({ quest: { ...q, steps } });
      const KINDS: Array<{ value: string; label: string }> = [
        { value: 'scan', label: 'Скан кода' },
        { value: 'quiz', label: 'Квиз' },
        { value: 'checkin', label: 'Отметка дня' },
      ];
      return (
        <>
          <div className="lab" style={{ marginBottom: 8 }}>
            Шаги по порядку — пропустить шаг нельзя
          </div>
          {q.steps.map((step, i) => (
            <div key={i} className="row" style={{ gap: 8, marginBottom: 8 }}>
              <select
                className="select"
                style={{ width: 140 }}
                value={step.kind}
                onChange={(e) =>
                  setSteps(q.steps.map((x, j) => (i === j ? { ...x, kind: e.target.value } : x)))
                }
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                style={{ flex: 1 }}
                value={step.title}
                placeholder="Что сделать на этом шаге"
                onChange={(e) =>
                  setSteps(q.steps.map((x, j) => (i === j ? { ...x, title: e.target.value } : x)))
                }
              />
              <input
                className="input mono"
                style={{ width: 90 }}
                type="number"
                value={step.reward}
                title="Баллы за шаг"
                onChange={(e) =>
                  setSteps(q.steps.map((x, j) => (i === j ? { ...x, reward: Number(e.target.value) } : x)))
                }
              />
              <Button size="sm" variant="danger" onClick={() => setSteps(q.steps.filter((_, j) => j !== i))}>
                ×
              </Button>
            </div>
          ))}
          {errors.steps && <div className="err">{errors.steps}</div>}
          <Button
            size="sm"
            icon="plus"
            onClick={() => setSteps([...q.steps, { kind: 'scan', title: '', reward: 20 }])}
          >
            Добавить шаг
          </Button>

          <Field label="Приз за всю цепочку" hint="Выдаётся сразу после последнего шага">
            <input
              className="input"
              value={q.prize ?? ''}
              onChange={(e) => patchConfig({ quest: { ...q, prize: e.target.value } })}
              placeholder="Фирменный набор"
            />
          </Field>
          <p className="sub">
            Шаг «квиз» требует вопросов, шаг «отметка дня» — настроек серии: иначе цепочка упрётся в
            шаг, который невозможно пройти, и сервер такую акцию не запустит.
          </p>
        </>
      );
    }

    default:
      return null;
  }
}

/** Таблица «приз → вес», с живым пересчётом вероятностей. */
function WeightTable({
  label,
  rows,
  error,
  onChange,
}: {
  label: string;
  rows: WeightedPrize[];
  error?: string;
  onChange: (rows: WeightedPrize[]) => void;
}) {
  const total = rows.reduce((sum, r) => sum + Math.max(0, r.weight), 0);

  const update = (index: number, changes: Partial<WeightedPrize>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...changes } : r)));
  };

  return (
    <Field label={label} error={error} hint="Вероятности считаются из весов и публикуются на странице акции.">
      {rows.map((row, i) => (
        <div className="wrow-edit" key={i}>
          <input
            className="input"
            value={row.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Название приза"
          />
          <input
            className="input mono"
            type="number"
            min={0}
            value={row.weight}
            onChange={(e) => update(i, { weight: Number(e.target.value) })}
            aria-label="Вес"
          />
          <span className="pct">{percent(row.weight, total)}%</span>
          <Button
            variant="ghost"
            size="sm"
            icon="close"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            title="Удалить"
          />
        </div>
      ))}
      <div className="row" style={{ marginTop: 8 }}>
        <Button size="sm" onClick={() => onChange([...rows, { label: '', weight: 10 }])}>
          + Вариант
        </Button>
        <span className="spacer" />
        <span className="faint mono" style={{ fontSize: 12 }}>
          сумма весов {num(total)}
        </span>
      </div>
    </Field>
  );
}

function ListEditor({
  label,
  hint,
  items,
  onChange,
}: {
  label: string;
  hint?: string;
  items: string[];
  onChange: (items: string[]) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      {items.map((item, i) => (
        <div className="row" style={{ marginBottom: 8 }} key={i}>
          <input
            className="input"
            value={item}
            onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
          />
          <Button
            variant="ghost"
            size="sm"
            icon="close"
            title="Удалить"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <Button size="sm" onClick={() => onChange([...items, ''])}>
        + Добавить
      </Button>
    </Field>
  );
}

// ─── шаг 3: оформление ───

function StepDesign({
  draft,
  brandHasLogo,
  patch,
}: {
  draft: CampaignDraft;
  brandHasLogo: boolean;
  patch: (changes: Partial<CampaignDraft>) => void;
}) {
  const addPage = (template: { title: string; body: string }) => {
    if (draft.pages.length >= 5) return;
    const page: CampaignPage = { position: draft.pages.length, title: template.title, body: template.body };
    patch({ pages: [...draft.pages, page] });
  };

  const updatePage = (index: number, changes: Partial<CampaignPage>) => {
    patch({ pages: draft.pages.map((p, i) => (i === index ? { ...p, ...changes } : p)) });
  };

  return (
    <>
      <Card>
        <div className="h2" style={{ marginBottom: 12 }}>
          Оформление страницы
        </div>

        <Field label="Логотип">
          <Segmented
            value={draft.logo_mode}
            options={[
              { value: 'brand', label: brandHasLogo ? 'Логотип бренда' : 'Монограмма' },
              { value: 'custom', label: 'Свой' },
              { value: 'none', label: 'Без логотипа' },
            ]}
            onChange={(mode) => patch({ logo_mode: mode })}
          />
        </Field>

        {draft.logo_mode === 'custom' && (
          <Field label="Ссылка на логотип" hint="PNG или SVG на прозрачном фоне">
            <input
              className="input mono"
              value={draft.logo_url}
              onChange={(e) => patch({ logo_url: e.target.value })}
              placeholder="https://…/logo.png"
            />
          </Field>
        )}

        <Field
          label="Видео на странице"
          hint="Файл до 50 МБ или ссылка на YouTube либо Vimeo. Покажем прямо на странице акции."
        >
          <MediaField
            value={draft.video_url ?? ''}
            accept="video/mp4,video/webm"
            placeholder="https://youtu.be/… или загрузи файл"
            onChange={(video_url) => patch({ video_url })}
          />
        </Field>

        <Field label="Текст кнопки" hint="Пусто — подставим подходящий для механики">
          <input
            className="input"
            value={draft.cta_text}
            onChange={(e) => patch({ cta_text: e.target.value })}
            placeholder="Участвовать"
          />
        </Field>
      </Card>

      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">Дополнительные страницы</span>
          <span className="spacer" />
          <span className="faint" style={{ fontSize: 12 }}>
            {draft.pages.length} из 5
          </span>
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          Строка с «?» на конце становится заголовком, строка с «- » — пунктом списка. Пустая строка
          разделяет блоки.
        </p>

        {draft.pages.map((page, i) => (
          <div key={i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <input
                className="input"
                value={page.title}
                onChange={(e) => updatePage(i, { title: e.target.value })}
                placeholder="Заголовок страницы"
              />
              <Button
                variant="ghost"
                size="sm"
                icon="close"
                title="Удалить страницу"
                onClick={() => patch({ pages: draft.pages.filter((_, j) => j !== i) })}
              />
            </div>
            <textarea
              className="textarea"
              value={page.body}
              onChange={(e) => updatePage(i, { body: e.target.value })}
              rows={5}
            />
          </div>
        ))}

        {draft.pages.length < 5 && (
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {PAGE_TEMPLATES.map((t) => (
              <Button key={t.title} size="sm" onClick={() => addPage(t)}>
                + {t.title}
              </Button>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ─── шаг 4: условия ───

function StepTerms({
  draft,
  errors,
  rules,
  rulesHash,
  rulesEdited,
  patch,
  onEditRules,
  onResetRules,
}: {
  draft: CampaignDraft;
  errors: Record<string, string>;
  rules: string;
  rulesHash?: string;
  rulesEdited: boolean;
  patch: (changes: Partial<CampaignDraft>) => void;
  onEditRules: (text: string) => void;
  onResetRules: () => void;
}) {
  return (
    <>
      <Card>
        <div className="h2" style={{ marginBottom: 12 }}>
          Организатор и выдача
        </div>
        <Field label="Организатор акции" error={errors.organizer}>
          <input
            className="input"
            value={draft.organizer}
            onChange={(e) => patch({ organizer: e.target.value })}
            placeholder="ООО «Название»"
          />
        </Field>
        <Field label="Реквизиты" hint="Необязательно, но попадёт в правила">
          <input
            className="input"
            value={draft.legal_id}
            onChange={(e) => patch({ legal_id: e.target.value })}
            placeholder="ИНН / ЕГРН"
          />
        </Field>
        <Field label="Контакт для вопросов по акции" error={errors.contact}>
          <input
            className="input"
            value={draft.contact}
            onChange={(e) => patch({ contact: e.target.value })}
            placeholder="promo@brand.tj · +992 …"
          />
        </Field>
        <Field label="Порядок выдачи призов">
          <textarea
            className="textarea"
            value={draft.handout_text}
            onChange={(e) => patch({ handout_text: e.target.value })}
            rows={3}
          />
        </Field>
        <Field label="Срок получения приза, дней" error={errors.handout_days}>
          <input
            className="input mono"
            type="number"
            min={1}
            value={draft.handout_days}
            onChange={(e) => patch({ handout_days: Number(e.target.value) })}
          />
        </Field>
      </Card>

      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">Правила акции</span>
          <span className="spacer" />
          {rulesEdited ? (
            <Button size="sm" onClick={onResetRules}>
              Вернуть сгенерированные
            </Button>
          ) : (
            <Badge tone="good">генерируются автоматически</Badge>
          )}
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          Текст собирается из полей выше по механике акции. Его можно править — при запуске он
          фиксируется и хешируется, чтобы участник мог доказать, с какой редакцией согласился.
        </p>
        <textarea
          className="textarea mono"
          style={{ minHeight: 320, fontSize: 11.5, lineHeight: 1.7 }}
          value={rules}
          onChange={(e) => onEditRules(e.target.value)}
          spellCheck={false}
        />
        {rulesHash && !rulesEdited && (
          <div className="krow" style={{ marginTop: 10 }}>
            <span className="k">Хеш правил</span>
            <span className="v mono">
              <Hash value={rulesHash} />
            </span>
          </div>
        )}
        {errors.rules_text && (
          <p className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {errors.rules_text}
          </p>
        )}
      </Card>
    </>
  );
}

function StepProof({
  draft,
  patch,
}: {
  draft: CampaignDraft;
  patch: (changes: Partial<CampaignDraft>) => void;
}) {
  return (
    <Card style={{ marginTop: 14 }}>
      <div className="h2" style={{ marginBottom: 4 }}>
        Доказательство призов
      </div>
      <p className="sub" style={{ marginBottom: 12 }}>
        Приложи фото призового фонда, накладную или ссылку на карточку товара. Мы посмотрим и
        поставим на карточке акции отметку «призы проверены» — покупатель увидит, что обещание не
        висит в воздухе. Это не обязательно, но без отметки акция выглядит как все остальные.
      </p>

      <Field label="Фото или ссылка">
        <MediaField
          value={draft.prize_proof_url ?? ''}
          accept="image/jpeg,image/png,image/webp"
          placeholder="https://… или загрузи фото"
          onChange={(prize_proof_url) => patch({ prize_proof_url })}
        />
      </Field>

      <Field label="Пояснение" hint="Что именно на фото и сколько призов закуплено">
        <textarea
          className="input"
          rows={2}
          value={draft.prize_proof_note ?? ''}
          onChange={(e) => patch({ prize_proof_note: e.target.value })}
          placeholder="12 смартфонов на складе в Душанбе, накладная от 3 октября"
        />
      </Field>
    </Card>
  );
}

// ─── шаг 5: запуск ───

function StepLaunch({
  draft,
  recipe,
  rules,
  errors,
  onGoto,
}: {
  draft: CampaignDraft;
  recipe: Recipe;
  rules: string;
  errors: Record<string, string>;
  onGoto: (step: number) => void;
}) {
  const rows = (draft.config[recipe.id === 'scratch' ? 'scratch_prizes' : 'segments'] ??
    []) as WeightedPrize[];

  const checks: Array<{ ok: boolean; text: string; step: number }> = [
    { ok: draft.name.trim().length > 0, text: 'Название акции заполнено', step: 2 },
    { ok: draft.regions.length > 0, text: 'Выбран хотя бы один регион', step: 2 },
    {
      ok:
        recipe.prize_mode === 'simple'
          ? draft.prize.trim().length > 0 && draft.winners > 0
          : rows.length > 0 && rows.some((r) => r.weight > 0),
      text: 'Призы заданы',
      step: 2,
    },
    {
      ok: !recipe.draw || Boolean(draft.draw_at),
      text: 'Дата розыгрыша указана',
      step: 2,
    },
    {
      ok:
        !recipe.draw ||
        !draft.draw_at ||
        new Date(draft.draw_at).getTime() >= new Date(draft.end_at).getTime(),
      text: 'Розыгрыш не раньше окончания приёма кодов',
      step: 2,
    },
    { ok: draft.organizer.trim().length > 0, text: 'Организатор указан', step: 4 },
    { ok: draft.contact.trim().length > 0, text: 'Контакт для вопросов указан', step: 4 },
    { ok: rules.trim().length > 0, text: 'Правила готовы', step: 4 },
  ];

  const allOk = checks.every((c) => c.ok);
  const scheduled = startsLater(draft.start_at);

  return (
    <>
      <Card>
        <div className="h2" style={{ marginBottom: 12 }}>
          Перед запуском
        </div>
        <ul className="check">
          {checks.map((c, i) => (
            <li key={i} className={c.ok ? 'ok' : 'no'}>
              <span className="m">
                <Icon name={c.ok ? 'check' : 'close'} size={12} />
              </span>
              <span style={{ flex: 1 }}>{c.text}</span>
              {!c.ok && (
                <Button variant="ghost" size="sm" onClick={() => onGoto(c.step)}>
                  Исправить
                </Button>
              )}
            </li>
          ))}
        </ul>

        {Object.keys(errors).length > 0 && (
          <p className="sub" style={{ color: 'var(--bad)', marginTop: 12 }}>
            Сервер отклонил запуск: {Object.values(errors).join('; ')}
          </p>
        )}
      </Card>

      <Card style={{ borderColor: allOk ? 'var(--head)' : undefined }}>
        <div className="h2" style={{ marginBottom: 8 }}>
          Что произойдёт при запуске
        </div>
        <ul className="check">
          <li className="ok">
            <span className="m">1</span>
            <span>
              Условия, призы и правила <b>фиксируются</b>. Изменить их потом нельзя — только копия в
              новую акцию.
            </span>
          </li>
          <li className="ok">
            <span className="m">2</span>
            <span>
              Сервер создаст секретный сид и опубликует его SHA-256 ещё до того, как кто-то сможет
              участвовать.
            </span>
          </li>
          <li className="ok">
            <span className="m">3</span>
            <span>
              Появится публичная страница акции и адрес для QR-кода на упаковке.
            </span>
          </li>
          <li className="ok">
            <span className="m">4</span>
            <span>
              {scheduled
                ? 'Акция будет запланирована и стартует сама в дату начала.'
                : 'Акция начнётся сразу: дата начала уже наступила.'}
            </span>
          </li>
          {recipe.draw && (
            <li className="ok">
              <span className="m">5</span>
              <span>
                В конце приёма кодов список билетов зафиксируется корнем Меркла. Розыгрыш нужно будет
                провести вручную, указав внешний публичный сид.
              </span>
            </li>
          )}
        </ul>
        <p className="sub" style={{ marginTop: 12 }}>
          Не забудь загрузить коды: без них участвовать нечем. Это можно сделать и после запуска, на
          странице акции.
        </p>
      </Card>
    </>
  );
}

// ─── вспомогательное ───

function toDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fromDateInput(value: string): string {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toISOString();
}

function toDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateTimeInput(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

/** Обратное преобразование: сохранённая акция → форма мастера. */
export function campaignToDraft(c: Campaign): CampaignDraft {
  return {
    id: c.id,
    recipe: c.recipe,
    name: c.name,
    tagline: c.tagline,
    cta_text: c.cta_text,
    prize: c.prize_title,
    winners: c.winner_count || 1,
    pts_per_code: c.pts_per_code,
    code_place: c.code_place,
    age18: c.age18,
    regions: [...c.regions],
    start_at: c.start_at,
    end_at: c.end_at,
    draw_at: c.draw_at ?? null,
    config: c.config,
    logo_mode: c.logo_mode,
    logo_url: c.logo_url ?? '',
    pages: c.pages ?? [],
    organizer: c.organizer,
    legal_id: c.legal_id,
    contact: c.contact,
    handout_text: c.handout_text,
    i18n: c.i18n ?? {},
    video_url: c.video_url ?? '',
    prize_proof_url: c.prize_proof_url ?? '',
    prize_proof_note: c.prize_proof_note ?? '',
    handout_days: c.handout_days,
    rules_text: c.rules_text,
  };
}
